const cacheStore = new Map();
const inflightRequests = new Map();

function nowMs() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function createAbortError() {
  const error = new Error("Request aborted");
  error.name = "AbortError";
  return error;
}

function getErrorDetail(error) {
  if (error?.response?.status) {
    return `status=${error.response.status}`;
  }
  if (error?.message) {
    return error.message;
  }
  return "request_failed";
}

function logRequestEvent(event, { path, source, key, durationMs, detail } = {}) {
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  const parts = [`[API TRACE] ${event}`];

  if (path) parts.push(`path=${path}`);
  if (source) parts.push(`source=${source}`);
  if (key) parts.push(`key=${key}`);
  if (durationMs != null) parts.push(`duration=${durationMs.toFixed(1)}ms`);
  if (detail) parts.push(detail);

  console.debug(parts.join(" "));
}

export function isAbortError(error) {
  return Boolean(
    error?.name === "AbortError" ||
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED" ||
      error?.message === "canceled" ||
      error?.message === "Request aborted"
  );
}

function ensureNotAborted(signal) {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function waitForInflight(request, { signal }) {
  request.consumers += 1;

  let released = false;
  let abortListener;

  const release = () => {
    if (released) {
      return;
    }

    released = true;

    if (signal && abortListener) {
      signal.removeEventListener("abort", abortListener);
    }

    request.consumers = Math.max(0, request.consumers - 1);

    if (request.consumers === 0 && !request.settled && !request.controller.signal.aborted) {
      request.controller.abort();
    }
  };

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      release();
      reject(createAbortError());
      return;
    }

    abortListener = () => {
      release();
      reject(createAbortError());
    };

    if (signal) {
      signal.addEventListener("abort", abortListener, { once: true });
    }

    request.promise.then(
      (value) => {
        release();
        resolve(value);
      },
      (error) => {
        release();
        reject(error);
      }
    );
  });
}

export function getCachedValue(key, ttlMs) {
  const entry = cacheStore.get(key);
  if (!entry) return null;
  if (Date.now() - entry.createdAt > ttlMs) {
    cacheStore.delete(key);
    return null;
  }
  return entry.value;
}

export function setCachedValue(key, value) {
  cacheStore.set(key, { value, createdAt: Date.now() });
  return value;
}

export function clearCachedValue(key) {
  if (key) {
    cacheStore.delete(key);
  }
}

export function clearCachedValues(keys = []) {
  keys.filter(Boolean).forEach((key) => cacheStore.delete(key));
}

// Drops EVERYTHING cached in this tab and cancels requests still in flight.
// The cache is keyed by endpoint, not by user, and the SPA is not reloaded on
// logout, so without this the next account to sign in on the same tab would be
// handed the previous account's cached lists (e.g. an admin's full PO list for
// a coordinator who is only allowed to see their own POs) until the TTL ran out.
export function clearAllCachedValues() {
  cacheStore.clear();
  inflightRequests.forEach((request) => {
    try {
      request.controller.abort();
    } catch {
      /* already settled */
    }
  });
  inflightRequests.clear();
}

export async function runTrackedRequest({ path, source = "unknown", request, signal }) {
  ensureNotAborted(signal);
  const startedAt = nowMs();

  try {
    const result = await request({ signal });
    logRequestEvent("REQUEST_DONE", {
      path,
      source,
      durationMs: nowMs() - startedAt,
    });
    return result;
  } catch (error) {
    if (isAbortError(error) || signal?.aborted) {
      logRequestEvent("REQUEST_ABORT", {
        path,
        source,
        durationMs: nowMs() - startedAt,
      });
      throw createAbortError();
    }

    logRequestEvent("REQUEST_ERROR", {
      path,
      source,
      durationMs: nowMs() - startedAt,
      detail: getErrorDetail(error),
    });
    throw error;
  }
}

export async function getOrFetchCached({
  key,
  ttlMs,
  fetcher,
  force = false,
  source = "unknown",
  path = key,
  signal,
}) {
  ensureNotAborted(signal);

  if (!force) {
    const cached = getCachedValue(key, ttlMs);
    if (cached !== null) {
      logRequestEvent("CACHE_HIT", { path, source, key });
      return { data: cached, fromCache: true };
    }
  }

  // A forced call (e.g. right after saving an edit) must never be satisfied
  // by an older, unforced request that was already in flight — that request
  // started before the edit and its response would silently overwrite the
  // fresh data once it resolves. Only dedupe against another in-flight
  // request when this call isn't the one demanding a bypass.
  const existing = !force && inflightRequests.get(key);
  if (existing) {
    logRequestEvent("DUPLICATE_SUPPRESSED", { path, source, key });
    const data = await waitForInflight(existing, { signal });
    return { data, fromCache: false };
  }

  logRequestEvent(force ? "CACHE_BYPASS" : "CACHE_MISS", { path, source, key });

  const controller = new AbortController();
  const startedAt = nowMs();
  const request = {
    controller,
    consumers: 0,
    settled: false,
    promise: null,
  };

  request.promise = (async () => {
    try {
      const data = await fetcher({ signal: controller.signal });
      // A forced call replaces whatever was tracked for this key without
      // waiting for it to finish (see above), so an older, superseded
      // request can still resolve afterward. Only let the request that is
      // still the current one for this key write the cache — otherwise a
      // slow straggler would clobber the fresher value with stale data.
      if (inflightRequests.get(key) === request) {
        setCachedValue(key, data);
      }
      logRequestEvent("REQUEST_DONE", {
        path,
        source,
        key,
        durationMs: nowMs() - startedAt,
      });
      return data;
    } catch (error) {
      if (isAbortError(error) || controller.signal.aborted) {
        logRequestEvent("REQUEST_ABORT", {
          path,
          source,
          key,
          durationMs: nowMs() - startedAt,
        });
        throw createAbortError();
      }

      logRequestEvent("REQUEST_ERROR", {
        path,
        source,
        key,
        durationMs: nowMs() - startedAt,
        detail: getErrorDetail(error),
      });
      throw error;
    } finally {
      request.settled = true;
      if (inflightRequests.get(key) === request) {
        inflightRequests.delete(key);
      }
    }
  })();

  inflightRequests.set(key, request);

  const data = await waitForInflight(request, { signal });
  return { data, fromCache: false };
}
